import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  ShieldCheck, Plus, Pin, Paperclip, CheckCircle2, Users, Target,
  FileText, Pencil, Trash2, ClipboardCheck, Settings2,
} from 'lucide-react';
import { toast } from 'sonner';
import { qmbAPI } from '../lib/api';
import { CATEGORY_STYLES, fmtDate } from './qmb/qmbConstants';
import { PostEditorDialog } from './qmb/PostEditorDialog';
import { ConfirmationsDialog } from './qmb/ConfirmationsDialog';
import { EditorsDialog } from './qmb/EditorsDialog';

const catLabel = (meta, key) => (meta?.categories || []).find((c) => c.key === key)?.label || 'Info';

const PostCard = ({ post, meta, canPublish, onConfirm, onEdit, onDelete, onShowConfirmations }) => (
  <Card className="border-border/60" data-testid={`qmb-post-${post.id}`}>
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          {post.is_pinned && <Pin className="h-4 w-4 text-amber-500 shrink-0" />}
          <Badge variant="outline" className={`${CATEGORY_STYLES[post.category] || ''} shrink-0`}>
            {catLabel(meta, post.category)}
          </Badge>
          {post.require_confirmation && (
            <Badge variant="outline" className="text-amber-700 border-amber-300 gap-1 shrink-0">
              <ClipboardCheck className="h-3 w-3" /> Lesebestätigung
            </Badge>
          )}
        </div>
        {canPublish && (
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(post)} data-testid={`qmb-edit-${post.id}`}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => onDelete(post)} data-testid={`qmb-delete-${post.id}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      <h3 className="font-heading font-semibold text-lg mt-2">{post.title}</h3>
    </CardHeader>
    <CardContent className="space-y-3">
      {post.content && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{post.content}</p>}

      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        {post.target_all ? (
          <Badge variant="secondary" className="gap-1"><Users className="h-3 w-3" /> Alle Mitarbeiter</Badge>
        ) : (
          (post.target_departments || []).map((n) => (
            <Badge key={n} variant="secondary" className="gap-1"><Target className="h-3 w-3" /> {n}</Badge>
          ))
        )}
      </div>

      {(post.attachments || []).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {post.attachments.map((a) => (
            <a key={a.id} href={qmbAPI.fileUrl(a.id)} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline border rounded px-2 py-1"
              data-testid={`qmb-attachment-${a.id}`}>
              <Paperclip className="h-3 w-3" /> {a.original_filename}
            </a>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/40 mt-2">
        <p className="text-xs text-muted-foreground flex items-center gap-2">
          <FileText className="h-3 w-3" /> {post.author_name} · {fmtDate(post.created_at)}
        </p>
        <div className="flex items-center gap-2">
          {canPublish && post.require_confirmation && (
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => onShowConfirmations(post)} data-testid={`qmb-confirmations-${post.id}`}>
              <ClipboardCheck className="h-3.5 w-3.5 mr-1" />
              {post.confirmation_count ?? 0}/{post.audience_total ?? 0} bestätigt
            </Button>
          )}
          {post.require_confirmation && (
            post.user_confirmed ? (
              <Badge className="bg-emerald-100 text-emerald-700 gap-1"><CheckCircle2 className="h-3 w-3" /> Bestätigt</Badge>
            ) : (
              <Button size="sm" onClick={() => onConfirm(post)} data-testid={`qmb-confirm-${post.id}`}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Gelesen &amp; bestätigen
              </Button>
            )
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function QMB() {
  const [meta, setMeta] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [confPost, setConfPost] = useState(null);
  const [editorsOpen, setEditorsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, p] = await Promise.all([qmbAPI.meta(), qmbAPI.listPosts()]);
      setMeta(m.data);
      setPosts(p.data.posts || []);
    } catch (e) {
      toast.error('QMB-Infos konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const canPublish = meta?.can_publish;

  const handleConfirm = async (post) => {
    try {
      await qmbAPI.confirmPost(post.id);
      toast.success('Lesebestätigung gespeichert');
      load();
    } catch (e) {
      toast.error('Bestätigung fehlgeschlagen');
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`QMB-Info „${post.title}" wirklich löschen?`)) return;
    try {
      await qmbAPI.deletePost(post.id);
      toast.success('Gelöscht');
      load();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const openNew = () => { setEditPost(null); setEditorOpen(true); };
  const openEdit = (post) => { setEditPost(post); setEditorOpen(true); };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="qmb-page">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="font-heading font-bold text-3xl flex items-center gap-2">
              <ShieldCheck className="h-7 w-7 text-primary" /> QMB / Qualitätsmanagement
            </h1>
            <p className="text-muted-foreground mt-1">
              Qualitätsmanagement-Infos gezielt an Mitarbeiter bestimmter Maßnahmen/Abteilungen.
            </p>
          </div>
          {canPublish && (
            <div className="flex items-center gap-2 shrink-0">
              {meta?.is_admin && (
                <Button variant="outline" onClick={() => setEditorsOpen(true)} data-testid="qmb-manage-editors-btn">
                  <Settings2 className="h-4 w-4 mr-1" /> Verantwortliche
                </Button>
              )}
              <Button onClick={openNew} data-testid="qmb-new-btn">
                <Plus className="h-4 w-4 mr-1" /> Neue QMB-Info
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="space-y-4"><Skeleton className="h-40" /><Skeleton className="h-40" /></div>
        ) : posts.length === 0 ? (
          <Alert data-testid="qmb-empty">
            <ShieldCheck className="h-4 w-4" />
            <AlertTitle>Keine QMB-Infos</AlertTitle>
            <AlertDescription>
              {canPublish
                ? 'Noch keine QMB-Infos veröffentlicht. Erstelle die erste über „Neue QMB-Info".'
                : 'Aktuell liegen keine für dich relevanten QMB-Infos vor.'}
            </AlertDescription>
          </Alert>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {posts.map((post) => (
              <PostCard
                key={post.id} post={post} meta={meta} canPublish={canPublish}
                onConfirm={handleConfirm} onEdit={openEdit} onDelete={handleDelete}
                onShowConfirmations={setConfPost}
              />
            ))}
          </div>
        )}
      </div>

      <PostEditorDialog open={editorOpen} onOpenChange={setEditorOpen} meta={meta} post={editPost} onSaved={load} />
      {confPost && (
        <ConfirmationsDialog open={!!confPost} onOpenChange={(o) => { if (!o) setConfPost(null); }} post={confPost} />
      )}
      <EditorsDialog open={editorsOpen} onOpenChange={setEditorsOpen} />
    </DashboardLayout>
  );
}
